/**
 * Client Followup Planning and Support Service
 * Manages client followup scheduling and reminders
 */

import { supabase } from './supabase'

/**
 * Create a followup
 */
export const createFollowup = async (organizationId, clientId, followupData) => {
  try {
    const { data, error } = await supabase
      .from('client_followups')
      .insert({
        organization_id: organizationId,
        client_id: clientId,
        followup_type: followupData.type,
        scheduled_date: followupData.scheduledDate,
        notes: followupData.notes,
        status: 'scheduled',
        created_by: followupData.createdBy,
      })
      .select()
      .single()

    if (error) throw error
    return { success: true, data }
  } catch (error) {
    console.error('Error creating followup:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Get client followups
 */
export const getClientFollowups = async (organizationId, clientId = null, filters = {}) => {
  try {
    let query = supabase
      .from('client_followups')
      .select('*, clients(first_name, last_name)')
      .eq('organization_id', organizationId)
      .order('scheduled_date', { ascending: true })

    if (clientId) {
      query = query.eq('client_id', clientId)
    }

    if (filters.status) {
      query = query.eq('status', filters.status)
    }

    if (filters.type) {
      query = query.eq('followup_type', filters.type)
    }

    if (filters.startDate) {
      query = query.gte('scheduled_date', filters.startDate)
    }

    if (filters.endDate) {
      query = query.lte('scheduled_date', filters.endDate)
    }

    const { data, error } = await query

    if (error) throw error
    return { success: true, data: data || [] }
  } catch (error) {
    console.error('Error getting followups:', error)
    return { success: false, error: error.message, data: [] }
  }
}

/**
 * Get upcoming followups
 */
export const getUpcomingFollowups = async (organizationId, days = 7) => {
  try {
    const startDate = new Date()
    const endDate = new Date()
    endDate.setDate(endDate.getDate() + days)

    const { data, error } = await supabase
      .from('client_followups')
      .select('*, clients(first_name, last_name, email, phone)')
      .eq('organization_id', organizationId)
      .eq('status', 'scheduled')
      .gte('scheduled_date', startDate.toISOString())
      .lte('scheduled_date', endDate.toISOString())
      .order('scheduled_date', { ascending: true })

    if (error) throw error
    return { success: true, data: data || [] }
  } catch (error) {
    console.error('Error getting upcoming followups:', error)
    return { success: false, error: error.message, data: [] }
  }
}

/**
 * Update followup status
 */
export const updateFollowupStatus = async (followupId, status, organizationId) => {
  try {
    const updateData = {
      status,
      updated_at: new Date().toISOString(),
    }

    if (status === 'completed') {
      updateData.completed_date = new Date().toISOString()
    }

    const { data, error } = await supabase
      .from('client_followups')
      .update(updateData)
      .eq('id', followupId)
      .eq('organization_id', organizationId)
      .select()
      .single()

    if (error) throw error
    return { success: true, data }
  } catch (error) {
    console.error('Error updating followup status:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Mark reminder as sent
 */
export const markReminderSent = async (followupId, organizationId) => {
  try {
    const { data, error } = await supabase
      .from('client_followups')
      .update({
        reminder_sent: true,
        reminder_sent_at: new Date().toISOString(),
      })
      .eq('id', followupId)
      .eq('organization_id', organizationId)
      .select()
      .single()

    if (error) throw error
    return { success: true, data }
  } catch (error) {
    console.error('Error marking reminder sent:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Get followups needing reminders
 */
export const getFollowupsNeedingReminders = async (organizationId, daysAhead = 1) => {
  try {
    const startDate = new Date()
    const endDate = new Date()
    endDate.setDate(endDate.getDate() + daysAhead)

    const { data, error } = await supabase
      .from('client_followups')
      .select('*, clients(first_name, last_name, email, phone)')
      .eq('organization_id', organizationId)
      .eq('status', 'scheduled')
      .eq('reminder_sent', false)
      .gte('scheduled_date', startDate.toISOString())
      .lte('scheduled_date', endDate.toISOString())

    if (error) throw error
    return { success: true, data: data || [] }
  } catch (error) {
    console.error('Error getting followups needing reminders:', error)
    return { success: false, error: error.message, data: [] }
  }
}

/**
 * Delete followup
 */
export const deleteFollowup = async (followupId, organizationId) => {
  try {
    const { error } = await supabase
      .from('client_followups')
      .delete()
      .eq('id', followupId)
      .eq('organization_id', organizationId)

    if (error) throw error
    return { success: true }
  } catch (error) {
    console.error('Error deleting followup:', error)
    return { success: false, error: error.message }
  }
}

export default {
  createFollowup,
  getClientFollowups,
  getUpcomingFollowups,
  updateFollowupStatus,
  markReminderSent,
  getFollowupsNeedingReminders,
  deleteFollowup,
}

